import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import InstallPWA from "./components/InstallPWA";
import Login from "./pages/Login";
import FactoryLogin from "./pages/FactoryLogin";
import FactoryDisplay from "./pages/FactoryDisplay";
import Dashboard from "./pages/Dashboard";
import Parts from "./pages/Parts";
import Molds from "./pages/Molds";
import Machines from "./pages/Machines";
import Orders from "./pages/Orders";
import OrderReport from "./pages/OrderReport";
import QualityInspections from "./pages/QualityInspections";
import ShopFloor from "./pages/ShopFloor";
import Quality from "./pages/Quality";
import Reports from "./pages/Reports";
import OperatorPanel from "./pages/OperatorPanel";
import MoldMaintenance from "./pages/MoldMaintenance";
import UserManagement from "./pages/UserManagement";
import CostControl from "./pages/CostControl";
import RawMaterials from "./pages/RawMaterials";
import ISO9001 from "./pages/ISO9001";
import Alerts from "./pages/Alerts";
import Planeamento from "./pages/Planeamento";
import MachineManagement from "./pages/MachineManagement";
import ShiftSchedule from "./pages/ShiftSchedule";
import Packaging from "./pages/Packaging";
import Settings from "./pages/Settings";
import PurchaseOrders from "./pages/PurchaseOrders";
import Messages from "./pages/Messages";
import DownloadApp from "./pages/DownloadApp";
import ShopFloor2 from "./pages/ShopFloor2";
import ShopFloor3 from "./pages/ShopFloor3";
import Stocks from "./pages/Stocks";
import FulfilledOrders from "./pages/FulfilledOrders";
import Billing from "./pages/Billing";
import SalesGuides from "./pages/SalesGuides";
import Clients from "./pages/Clients";
import SalesReport from "./pages/SalesReport";
import PartLabels from "./pages/PartLabels";
import WorkInstructions from "./pages/WorkInstructions";
import QualityControlPlans from "./pages/QualityControlPlans";
import BoxLabels from "./pages/BoxLabels";
import OEEDashboard from "./pages/OEEDashboard";
import PreventiveMaintenance from "./pages/PreventiveMaintenance";
import NotificationSettingsPage from "./pages/NotificationSettings";
import TimeTracking from "./pages/TimeTracking";
import EmployeeProfile from "./pages/EmployeeProfile";
import BOMEditor from "./pages/BOMEditor";
import ExecutiveDashboard from "./pages/ExecutiveDashboard";
import FactoryMap from "./pages/FactoryMap";
import Approvals from "./pages/Approvals";
import Reliability from "./pages/Reliability";
import Home from "./pages/Home";
import InviteAccept from "./pages/InviteAccept";
import BoxInfo from "./pages/BoxInfo";
import QrBadges from "./pages/QrBadges";
import Visitors from "./pages/Visitors";
import Gate from "./pages/Gate";
import AiAssistant from "./pages/AiAssistant";
import Spc from "./pages/Spc";
import ParetoWaste from "./pages/ParetoWaste";
import MoldFiche from "./pages/MoldFiche";
import MoldQrLabels from "./pages/MoldQrLabels";
import { PortalLogin, PortalRegister, PortalDashboard, PortalOrders, PortalOrderDetail } from "./pages/CustomerPortal";
import PortalInvites from "./pages/PortalInvites";
import Costing from "./pages/Costing";
import CMMS from "./pages/CMMS";
import Gantt from "./pages/Gantt";
import Wms from "./pages/Wms";
import OldShifts from "./pages/OldShifts";
import Layout from "./components/Layout";
import ChatBubble from "./components/ChatBubble";

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground">A carregar...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/factory-login" element={<FactoryLogin />} />
      <Route path="/factory-display" element={<FactoryDisplay />} />
      <Route path="/gate" element={<ProtectedRoute allowedRoles={["admin", "shift_leader", "quality"]}><Gate /></ProtectedRoute>} />
      {/* Customer Portal — public routes outside main Layout */}
      <Route path="/portal/login" element={<PortalLogin />} />
      <Route path="/portal/register" element={<PortalRegister />} />
      <Route path="/portal/dashboard" element={<PortalDashboard />} />
      <Route path="/portal/orders" element={<PortalOrders />} />
      <Route path="/portal/orders/:orderNumber" element={<PortalOrderDetail />} />
      <Route path="/invite/:token" element={<InviteAccept />} />
      <Route path="/box/:lot" element={<BoxInfo />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Home />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route
          path="parts"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader"]}>
              <Parts />
            </ProtectedRoute>
          }
        />
        <Route
          path="molds"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader"]}>
              <Molds />
            </ProtectedRoute>
          }
        />
        <Route
          path="machines"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader"]}>
              <Machines />
            </ProtectedRoute>
          }
        />
        <Route
          path="orders"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader"]}>
              <Orders />
            </ProtectedRoute>
          }
        />
        <Route
          path="orders/:orderId/report"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader", "operator"]}>
              <OrderReport />
            </ProtectedRoute>
          }
        />
        <Route 
          path="shop-floor" 
          element={
            <ProtectedRoute allowedRoles={["admin", "operator", "quality", "shift_leader", "display"]}>
              <ShopFloor />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="shop-floor-2" 
          element={
            <ProtectedRoute allowedRoles={["admin", "operator", "quality", "shift_leader", "display"]}>
              <ShopFloor2 />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="shop-floor-3" 
          element={
            <ProtectedRoute allowedRoles={["admin", "operator", "quality", "shift_leader", "display"]}>
              <ShopFloor3 />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="operator-panel" 
          element={
            <ProtectedRoute allowedRoles={["admin", "operator", "quality", "shift_leader"]}>
              <OperatorPanel />
            </ProtectedRoute>
          } 
        />
        <Route
          path="quality"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader"]}>
              <Quality />
            </ProtectedRoute>
          }
        />
        <Route
          path="quality-inspections"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader"]}>
              <QualityInspections />
            </ProtectedRoute>
          }
        />
        <Route
          path="mold-maintenance"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader"]}>
              <MoldMaintenance />
            </ProtectedRoute>
          }
        />
        <Route path="reports" element={<Reports />} />
        <Route
          path="users"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <UserManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="qr-badges"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <QrBadges />
            </ProtectedRoute>
          }
        />
        <Route
          path="visitors"
          element={
            <ProtectedRoute allowedRoles={["admin", "shift_leader", "quality"]}>
              <Visitors />
            </ProtectedRoute>
          }
        />
        <Route
          path="ai-assistant"
          element={
            <ProtectedRoute allowedRoles={["admin", "shift_leader", "quality", "operator"]}>
              <AiAssistant />
            </ProtectedRoute>
          }
        />
        <Route
          path="spc"
          element={
            <ProtectedRoute allowedRoles={["admin", "shift_leader", "quality"]}>
              <Spc />
            </ProtectedRoute>
          }
        />
        <Route
          path="pareto"
          element={
            <ProtectedRoute allowedRoles={["admin", "shift_leader", "quality"]}>
              <ParetoWaste />
            </ProtectedRoute>
          }
        />
        <Route
          path="gantt"
          element={
            <ProtectedRoute allowedRoles={["admin", "shift_leader", "quality"]}>
              <Gantt />
            </ProtectedRoute>
          }
        />
        <Route
          path="wms"
          element={
            <ProtectedRoute allowedRoles={["admin", "shift_leader", "quality", "operator"]}>
              <Wms />
            </ProtectedRoute>
          }
        />
        <Route
          path="old-shifts"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <OldShifts />
            </ProtectedRoute>
          }
        />
        <Route
          path="molds/:moldId/full"
          element={
            <ProtectedRoute allowedRoles={["admin", "shift_leader", "quality"]}>
              <MoldFiche />
            </ProtectedRoute>
          }
        />
        <Route
          path="molds-qr-labels"
          element={
            <ProtectedRoute allowedRoles={["admin", "shift_leader"]}>
              <MoldQrLabels />
            </ProtectedRoute>
          }
        />
        <Route
          path="portal-invites"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <PortalInvites />
            </ProtectedRoute>
          }
        />
        <Route
          path="costing"
          element={
            <ProtectedRoute allowedRoles={["admin", "shift_leader", "quality"]}>
              <Costing />
            </ProtectedRoute>
          }
        />
        <Route
          path="cmms"
          element={
            <ProtectedRoute allowedRoles={["admin", "shift_leader", "quality"]}>
              <CMMS />
            </ProtectedRoute>
          }
        />
        <Route
          path="cost-control"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <CostControl />
            </ProtectedRoute>
          }
        />
        <Route
          path="raw-materials"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader"]}>
              <RawMaterials />
            </ProtectedRoute>
          }
        />
        <Route
          path="iso9001"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader"]}>
              <ISO9001 />
            </ProtectedRoute>
          }
        />
        <Route
          path="alerts"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader"]}>
              <Alerts />
            </ProtectedRoute>
          }
        />
        <Route
          path="planeamento"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader"]}>
              <Planeamento />
            </ProtectedRoute>
          }
        />
        <Route
          path="machine-management"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader"]}>
              <MachineManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="shift-schedule"
          element={
            <ProtectedRoute allowedRoles={["admin", "operator", "quality", "shift_leader"]}>
              <ShiftSchedule />
            </ProtectedRoute>
          }
        />
        <Route
          path="packaging"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader"]}>
              <Packaging />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader"]}>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="purchase-orders"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <PurchaseOrders />
            </ProtectedRoute>
          }
        />
        <Route
          path="messages"
          element={
            <ProtectedRoute allowedRoles={["admin", "operator", "quality", "shift_leader"]}>
              <Messages />
            </ProtectedRoute>
          }
        />
        <Route
          path="download"
          element={
            <ProtectedRoute>
              <DownloadApp />
            </ProtectedRoute>
          }
        />
        <Route
          path="stocks"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader"]}>
              <Stocks />
            </ProtectedRoute>
          }
        />
        <Route
          path="fulfilled-orders"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader"]}>
              <FulfilledOrders />
            </ProtectedRoute>
          }
        />
        <Route
          path="billing"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <Billing />
            </ProtectedRoute>
          }
        />
        <Route
          path="sales-guides"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader"]}>
              <SalesGuides />
            </ProtectedRoute>
          }
        />
        <Route
          path="clients"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader"]}>
              <Clients />
            </ProtectedRoute>
          }
        />
        <Route
          path="sales-report"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader"]}>
              <SalesReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="part-labels"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader", "operator"]}>
              <PartLabels />
            </ProtectedRoute>
          }
        />
        <Route
          path="work-instructions"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader"]}>
              <WorkInstructions />
            </ProtectedRoute>
          }
        />
        <Route
          path="quality-control-plans"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader"]}>
              <QualityControlPlans />
            </ProtectedRoute>
          }
        />
        <Route
          path="box-labels"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader", "operator"]}>
              <BoxLabels />
            </ProtectedRoute>
          }
        />
        <Route
          path="oee"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader"]}>
              <OEEDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="preventive-maintenance"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader"]}>
              <PreventiveMaintenance />
            </ProtectedRoute>
          }
        />
        <Route
          path="notification-settings"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <NotificationSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="time-tracking"
          element={
            <ProtectedRoute allowedRoles={["admin", "shift_leader"]}>
              <TimeTracking />
            </ProtectedRoute>
          }
        />
        <Route
          path="employees/:userId"
          element={
            <ProtectedRoute allowedRoles={["admin", "shift_leader"]}>
              <EmployeeProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="bom"
          element={
            <ProtectedRoute allowedRoles={["admin", "shift_leader", "quality"]}>
              <BOMEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="executive"
          element={
            <ProtectedRoute allowedRoles={["admin", "shift_leader"]}>
              <ExecutiveDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="factory-map"
          element={
            <ProtectedRoute allowedRoles={["admin", "operator", "quality", "shift_leader", "display"]}>
              <FactoryMap />
            </ProtectedRoute>
          }
        />
        <Route
          path="reliability"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader"]}>
              <Reliability />
            </ProtectedRoute>
          }
        />
        <Route
          path="approvals"
          element={
            <ProtectedRoute allowedRoles={["admin", "quality", "shift_leader"]}>
              <Approvals />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function ChatWrapper() {
  const { user } = useAuth();
  
  // Só mostrar chat se utilizador estiver autenticado e não for em páginas especiais
  if (!user) return null;
  
  return <ChatBubble user={user} />;
}

// Keep-alive: pinga o backend a cada 4 minutos para manter os serviços acordados
function KeepAlive() {
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    if (!API) return;
    
    const ping = () => {
      fetch(`${API}/api/health`).catch(() => {});
    };
    
    ping(); // ping imediato
    const interval = setInterval(ping, 60 * 1000); // 1 minuto
    return () => clearInterval(interval);
  }, []);
  
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <ChatWrapper />
        <KeepAlive />
        <InstallPWA />
        <Toaster 
          position="top-right" 
          richColors 
          closeButton
          toastOptions={{
            duration: 3000,
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;

// Registo do Service Worker com periodic sync para keep-alive
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then(async (reg) => {
      if ('periodicSync' in reg) {
        try {
          await reg.periodicSync.register('keep-alive', { minInterval: 2 * 60 * 1000 });
        } catch (e) {}
      }
    }).catch(() => {});
  });
}
